/**
 * TAX, AS A LINE THE CUSTOMER CAN SEE.
 *
 * Wrench Services is in New Mexico, where gross receipts tax is charged on
 * the whole of a bill -- parts and labor alike. Matt was typing that line
 * onto every quote by hand. This keeps it there for him: one tax line,
 * equal to the rate times everything else on the quote, kept in step as
 * the quote changes.
 *
 * It is a REAL line item, not a number bolted onto the total, so it rides
 * the same rails everything else does: it shows on the customer's estimate,
 * it is part of the figure they approve, and it lands on the invoice --
 * with no special case anywhere. He can still edit or delete it like any
 * line; this only writes it when a priced line changes.
 *
 * A shop charges tax only if it has a `grt_tax` automation carrying the
 * rate. Every other shop has none, so nothing here ever touches their
 * quotes. Kept out of the accounts table on purpose: the rate lives in the
 * one place a shop already switches its automation behaviour, and needs no
 * migration to land.
 */
import { db } from "../data/client.ts";

const TAX_RE = /gross\s*receipt|\bgrt\b|\btax\b/i;

/** Is this line the tax line? Matches a hand-typed one too, so we adopt it
 *  rather than adding a second. */
export const isTaxLine = (desc: string | null | undefined): boolean =>
  TAX_RE.test(desc ?? "");

type ShopTax = { rate: number; label: string };
let cached: ShopTax | null | undefined;

/** The shop's standing tax, or null if it charges none. Cached for the
 *  session; the rate changes about never. */
export async function shopTax(): Promise<ShopTax | null> {
  if (cached !== undefined) return cached;
  try {
    const sb = await db();
    const { data } = await sb.from("automations")
      .select("enabled,settings").eq("key", "grt_tax").limit(1).maybeSingle();
    const row = data as { enabled: boolean; settings: { rate?: number; label?: string } | null } | null;
    const rate = Number(row?.settings?.rate);
    cached = row && row.enabled && Number.isFinite(rate) && rate > 0
      ? { rate, label: (row.settings?.label || "Tax").trim() }
      : null;
  } catch { cached = null; }
  return cached;
}

/** Called if the rate is changed, so the session does not go stale. */
export const forgetShopTax = (): void => { cached = undefined; };

/** The description a tax line carries, e.g. "Gross Receipts Tax ... (7.44%)". */
const taxDesc = (t: ShopTax): string => `${t.label} (${(t.rate * 100).toFixed(2)}%)`;

type Row = { id: string; description: string; total: number; position: number; account_id: string };

/**
 * Keep the one tax line on a job equal to the rate times everything else
 * charged. Adopts a line already there, removes any duplicate, and takes
 * itself off a job with nothing priced. Never throws: a stale tax line is
 * something the shop fixes in a tap, and it must not stop a real line from
 * saving.
 */
export async function ensureTaxLine(jobId: string): Promise<void> {
  try {
    const tax = await shopTax();
    if (!tax) return;
    const sb = await db();
    const { data } = await sb.from("line_items")
      .select("id,description,total,position,account_id").eq("job_id", jobId);
    const lines = (data ?? []) as Row[];
    if (!lines.length) return;

    const taxLines = lines.filter((l) => isTaxLine(l.description));
    const base = lines.filter((l) => !isTaxLine(l.description))
      .reduce((n, l) => n + Number(l.total ?? 0), 0);

    if (base <= 0) {
      for (const t of taxLines) await sb.from("line_items").delete().eq("id", t.id);
      return;
    }

    const amount = Math.round(tax.rate * base * 100) / 100;
    const desc = taxDesc(tax);

    const keep = taxLines[0];
    if (!keep) {
      const acct = lines[0]?.account_id;
      if (!acct) return;
      const pos = Math.max(0, ...lines.map((l) => Number(l.position ?? 0))) + 1;
      await sb.from("line_items").insert({
        account_id: acct, job_id: jobId, kind: "fee",
        description: desc, quantity: 1, unit_price: amount, position: pos,
      });
      return;
    }

    await sb.from("line_items").update({
      kind: "fee", description: desc, quantity: 1, unit_price: amount,
    }).eq("id", keep.id);
    for (const e of taxLines.slice(1)) await sb.from("line_items").delete().eq("id", e.id);
  } catch { /* the tax line is never worth breaking a save over */ }
}
